import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { SwitchYearButtons } from '../../../../../components/UI-Elements/Buttons/SwitchYear';
import { FormatToShortPriceTemplate } from '../../../../../utils/FormatValue';
import { months } from '../../../../../utils/MonthYear/monthYear';
import { ExposureObj } from '../../../../FinancialPositions/components/FinancialPositionsUtils';

interface Props {
  materialsDataAsDictionary: Record<string, ExposureObj>;
  itemNames: Record<string, { name: string }>;
  colorsMap: Record<string, string>;
}

export const FutureOrdersSummeryBarChart: React.FC<Props> = ({ materialsDataAsDictionary, itemNames, colorsMap }) => {
  const [dataSet, setDataSet] = useState([]);
  const [displayedYear, setDisplayedYear] = useState(new Date().getFullYear());
  const [currentYearKeys, setCurrentYearKeys] = useState<string[]>([]);
  const [minMaxYear, setMinMaxYear] = useState({ min: 0, max: 0 });

  useEffect(() => {
    currentYearKeys && materialsDataAsDictionary && setDataSet(materialDataToDataSet());
  }, [currentYearKeys, materialsDataAsDictionary]);

  useEffect(() => {
    const allDatesList: number[] = Object.values(materialsDataAsDictionary).flatMap((material) =>
      Object.keys(material.exposureCalendar).map((date) => parseInt(date.substring(0, 4))),
    );
    setMinMaxYear(getMinMaxYears(allDatesList));
  }, [materialsDataAsDictionary]);

  useEffect(() => {
    setCurrentYearKeys(getMonthKeysOfYear(displayedYear));
  }, [displayedYear]);

  const materialDataToDataSet = (): [] => {
    const dataSets = Object.entries(materialsDataAsDictionary).map(([key, { exposureCalendar }]) => ({
      type: 'bar' as const,
      data: materialAndKeysToValueArray(currentYearKeys, exposureCalendar),
      backgroundColor: colorsMap[key],
      BorderColor: '#ffffff',
      yAxisID: 'y1',
      order: 2,
      barThickness: 10,
      borderWidth: 1,
      label: itemNames[key]?.name || key,
    }));
    return dataSets as [];
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
      },
    },
    scales: {
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        beginAtZero: true,
        title: {
          display: true,
          text: 'USD ',
        },
        ticks: {
          callback: function (value: any) {
            return FormatToShortPriceTemplate(value, '');
          },
        },
      },
      x: {
        grid: {
          display: false, // Disable the x-axis grid lines
        },
      },
    },
  };

  return (
    <div className="flex flex-col flex-1 ">
      <SwitchYearButtons
        currentYear={displayedYear}
        setCurrentYear={setDisplayedYear}
        maxYear={minMaxYear.max}
        minYear={minMaxYear.min}
      />
      <div className=" flex-1 h-[80%]   justify-self-start">
        <Bar options={options} data={{ labels: months, datasets: dataSet }} />
      </div>
    </div>
  );
};

const materialAndKeysToValueArray = (keysArray: string[], exposureCalendar: Record<string, number>): number[] =>
  keysArray.map((key) => exposureCalendar[key] || 0);

const getMinMaxYears = (allDates: number[]): { min: number; max: number } => {
  const minValue = Math.min(...allDates);
  const maxValue = Math.max(...allDates);
  return { min: parseInt(minValue.toString().slice(0, 4)), max: parseInt(maxValue.toString().slice(0, 4)) };
};

const getMonthKeysOfYear = (year: number): string[] =>
  Array.from({ length: 12 }).map((_, month) => `${year}${month + 1}`);
